import React, {ReactNode} from 'react'
import {Box, Button, IconButton, Stack, SxProps, Theme, Typography} from '@mui/material'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CloseIcon from '@mui/icons-material/Close'
import {NEUTRAL_SCALE, SEMANTIC} from 'src/theme-new/tokens'

type Severity = 'error' | 'warning' | 'success' | 'info'

const ICONS: Record<Severity, React.ElementType> = {
  error: ErrorOutlineIcon,
  warning: WarningAmberIcon,
  success: CheckCircleOutlineIcon,
  info: InfoOutlinedIcon,
}

interface InlineMessageProps {
  severity: Severity
  title?: string
  children: ReactNode
  onClose?: () => void
  // optional call to action shown under the text (e.g. a toast that leads somewhere)
  action?: {label: string; onClick: () => void}
  sx?: SxProps<Theme>
}

export default function InlineMessage({severity, title, children, onClose, action, sx}: InlineMessageProps): JSX.Element {
  const Icon = ICONS[severity]
  const colors = SEMANTIC[severity]

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.5,
      padding: '12px 16px',
      borderRadius: '8px',
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.light,
      ...sx,
    }}>
      <Icon sx={{color: colors.dark, fontSize: 20, mt: '1px', flexShrink: 0}} />
      <Stack spacing={0.5} sx={{flex: 1, minWidth: 0}}>
        {title && (
          <Typography variant="body2" sx={{fontWeight: 600, color: colors.dark}}>
            {title}
          </Typography>
        )}
        <Typography variant="body2" sx={{color: NEUTRAL_SCALE[700]}}>
          {children}
        </Typography>
        {action && (
          <Box>
            <Button size="small" onClick={action.onClick} sx={{p: 0, minWidth: 0, fontWeight: 600, color: colors.dark, textTransform: 'none'}}>
              {action.label}
            </Button>
          </Box>
        )}
      </Stack>
      {onClose && (
        <IconButton size="small" onClick={onClose} aria-label="close" sx={{ml: 'auto', flexShrink: 0, p: 0.5}}>
          <CloseIcon fontSize="small" sx={{color: NEUTRAL_SCALE[500]}} />
        </IconButton>
      )}
    </Box>
  )
}
